import { useAppDispatch, useTypedSelector } from '../../../store/store';
import { getAttempt, setAttemptMeta } from '../../analytics/telemetry';
import {
  changeStep,
  createResumableUploadUrl,
  CreateStoryRequest,
  resetUploadingIterations,
  setIsUploadInPorgress,
  setUploadError,
  setUploadFatal,
  setEtaSeconds,
  setIsSlowConnection,
  setIsResumingUpload,
  setProgress,
  UploadStoryDetails,
  // uploadVideoAsFile,
} from '../../../store/slices/upload';
// import Upload from 'gcs-browser-upload';
import { Upload, SessionExpiredError, UploadPausedError, ChunkMeasurement, WakeOutcome, WakeReason } from './Upload';
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import { EventNames } from '../../../common/constants/constants';
import {
  getCachedUploadSpeed,
  getVideoPosterWithCloudinary,
  isUploadCompleted,
  SMALL_CHUNK_BYTES,
} from '../../../pages/VideoToolPage/utils';
import {
  getSyncUpdatedVideoUrl,
  getVideoAspectRatio,
  getVideoDuration,
  isVideoARCloseToTargetAR,
} from '../../utilities';
import { useOnResumableUploadProgress } from './useUploadProgress';
import { useAttributesFilterUrl, useLogoFilterEnabled } from '../useAttributesFilterUrl';
import { usePostStory } from '../usePostStory';
import { useTrackEvent } from '../../../common/hooks/useTrackEvent';
import { UploadProcessStep } from '../../../store/storeModels';
import { DEFAULT_STORY_DETAILS } from '../../../api/constants';
import { setUploadMetrics } from '../../../store/slices/ui';
import { validateSlugAccountWithSubdomain } from '../../../common/utils/slugStorage';
import { useRouter } from 'next/router';
import { abortSpeedTest } from '../../upload/speedTestController';
import {
  write as writePendingUpload,
  patch as patchPendingUpload,
  PendingUpload,
} from '../../upload/pendingUploadStore';
import { fingerprintFile } from '../../upload/fingerprint';
import { getUploadDimensions } from '../../upload/uploadDimensions';
import {
  isCollapsingConnection,
  SLOW_CONSECUTIVE_TICKS,
  SLOW_OBSERVE_DELAY_MS,
  SLOW_WINDOW_MS,
  SLOW_SUSTAIN_MS,
  windowRateMbps,
  RateSample,
} from '../../upload/slowConnectionPolicy';
// import { isIOS, isSafari } from 'react-device-detect';

export interface VideoPos {
  x: number;
  y: number;
  width: number;
}

export interface HandleSubmitFileProps {
  file: File | null;
  details: UploadStoryDetails;
  isPublic: boolean;
  videoPos: VideoPos;
  shouldPostStory?: boolean;
  webappRecorded: boolean;
  /**
   * Cross-session resume: when provided, the persisted GCS resumable URI is
   * reused instead of minting a new one. Server-side 308 offset probe drives
   * where the new chunks start. Caller must have already verified that the
   * file fingerprint matches the persisted record.
   */
  resumeFrom?: { id: string; uploadUrl: string };
}

// const chunkSize = 15728640;

export const useHandleSubmitUploadedFile = (
  setStoryId: Dispatch<SetStateAction<string>>,
  isAthleticSolutionsClient: boolean,
  returnUrl: string,
) => {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const attributesFilterUrl = useAttributesFilterUrl();
  const logoEnabled = useLogoFilterEnabled();
  const { id } = useTypedSelector((state) => state.me);
  const { mainCategory } = useTypedSelector((state) => state.stories);
  const { account } = useTypedSelector((state) => state.account);
  const logoSrc = account.logo;
  const { id: categoryId } = mainCategory || {};

  // const onUploadProgress = useOnUploadProgress();
  const onResumableUploadProgress = useOnResumableUploadProgress();
  const postStory = usePostStory(setStoryId);
  const { trackEvent: rawTrackEvent } = useTrackEvent();

  // First-chunk gate for the "Preparing your video for upload" phase. When
  // a resume path sets `isResumingUpload = true`, the UI shows that copy.
  // We clear the flag as soon as the *first* chunk has actually moved bytes
  // server-side — proof that the upload is now flowing rather than still
  // probing / minting URLs. Reset to true at the start of each new upload
  // session below, so back-to-back uploads work.
  const firstProgressRef = useRef(true);
  // Wall-clock (ms) of the last sign of upload life: stamped when an upload
  // (re)starts and on every progress tick. The stall watchdog below reads it to
  // tell a live-but-slow upload from a wedged one.
  const lastProgressAtRef = useRef<number>(0);
  // Recovery activity counts as liveness for the session watchdog. A
  // stall → probe → shrink → retry chain means the upload machinery is
  // actively working the problem — not wedged — but it moves no bytes, so
  // without this stamp a chain on a dead link could cross WATCHDOG_STALL_MS
  // and put two recovery mechanisms (chunk retries + watchdog restart) in
  // flight at once. Bounded by the chunk retry budget (maxRetries), so a
  // runaway loop cannot hold the watchdog off forever: once retries exhaust,
  // the error path releases the lock itself.
  const lastSpeedMbsRef = useRef(0);
  const trackEvent: typeof rawTrackEvent = (props) => {
    if (props.action === EventNames.upload_chunk_stalled || props.action === EventNames.upload_chunk_retry) {
      lastProgressAtRef.current = Date.now();
    }
    // QA (Aug 19 field test): the "Part …" label only refreshed when a chunk
    // COMPLETED, so a mid-flight shrink was invisible for the entire retry —
    // minutes on a slow line. The retry event carries the new size; reflect it
    // in the meter immediately.
    if (props.action === EventNames.upload_chunk_retry && typeof props.shrunkToBytes === 'number') {
      dispatch(setUploadMetrics({ speedMbs: lastSpeedMbsRef.current, chunkSize: props.shrunkToBytes }));
    }
    rawTrackEvent(props);
  };

  // Mid-chunk slow detection state. On the links the sheet exists for, chunk
  // completions are minutes apart, so the consecutive-tick rule in
  // `onChunkMeasured` can never accumulate evidence in time — progress events
  // are the signal that actually arrives. See slowConnectionPolicy for the
  // window/delay rationale (socket-buffer burst).
  const progressSamplesRef = useRef<RateSample[]>([]);
  const observeStartAtRef = useRef(0);
  const slowSustainedSinceRef = useRef<number | null>(null);
  const lastLiveReadoutAtRef = useRef(0);

  const wrappedOnProgress = (event: Parameters<typeof onResumableUploadProgress>[0]) => {
    const now = Date.now();
    lastProgressAtRef.current = now;
    if (firstProgressRef.current) {
      firstProgressRef.current = false;
      dispatch(setIsResumingUpload(false));
      // Every (re)start refills socket buffers — restart the observation clock
      // and drop stale samples so a resume isn't judged by pre-pause readings.
      progressSamplesRef.current = [];
      observeStartAtRef.current = now;
      slowSustainedSinceRef.current = null;
    }
    // Sample on every progress event (not only while the sheet is un-fired):
    // the same sliding window now also drives the live meter readout below.
    const samples = progressSamplesRef.current;
    samples.push({ t: now, bytes: event.uploadedBytes });
    while (samples.length && samples[0].t < now - SLOW_WINDOW_MS) samples.shift();
    if (now - observeStartAtRef.current >= SLOW_OBSERVE_DELAY_MS) {
      const rate = windowRateMbps(samples);
      const remaining = event.totalBytes - event.uploadedBytes;
      if (rate !== null && remaining > 0) {
        // ── Live readout (QA: "decouple speed readout from chunk completion").
        // Pre-change the Mbps/ETA text refreshed only when a 5 MB part landed —
        // every 20–33 s on the connections users actually watch it on. Refresh
        // from the window rate every ~2 s instead. Same observation delay as
        // the sheet so the socket-buffer burst never prints a fantasy number.
        if (now - lastLiveReadoutAtRef.current >= 2000) {
          lastLiveReadoutAtRef.current = now;
          lastSpeedMbsRef.current = rate;
          dispatch(setUploadMetrics({ speedMbs: rate, chunkSize: event.chunkLength }));
          const etaSec = rate > 0 ? (remaining * 8) / (rate * 1_000_000) : null;
          if (etaSec !== null) dispatch(setEtaSeconds(Math.round(etaSec)));
        }
        if (!slowSheetFiredRef.current) {
          const etaSec = rate > 0 ? (remaining * 8) / (rate * 1_000_000) : null;
          if (isCollapsingConnection(rate, etaSec)) {
            if (slowSustainedSinceRef.current === null) slowSustainedSinceRef.current = now;
            if (now - slowSustainedSinceRef.current >= SLOW_SUSTAIN_MS) {
              slowSheetFiredRef.current = true;
              if (etaSec !== null) dispatch(setEtaSeconds(Math.round(etaSec)));
              lastSpeedMbsRef.current = rate;
              dispatch(setUploadMetrics({ speedMbs: rate, chunkSize: event.chunkLength }));
              dispatch(setIsSlowConnection(true));
            }
          } else {
            slowSustainedSinceRef.current = null;
          }
        }
      }
    }
    onResumableUploadProgress(event);
  };

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUrl, setResumeUrl] = useState<string>('');
  const [resumableUpload, setResumableUpload] = useState<Upload | null>();
  const isRestartingRef = useRef(false);
  const pendingStoryRetryRef = useRef<CreateStoryRequest | null>(null);
  const activeUploadIdRef = useRef<string | null>(null);
  const storyPostedRef = useRef(false);

  // One-shot initial dispatch when the Upload instance is created, so the
  // meter has something to show before the first chunk completes. The deps
  // intentionally use `resumableUpload` itself (instance identity) — depending
  // on `.speed` / `.chunkSize` primitive fields was broken because those are
  // *mutated* on the existing instance by `adjustChunkSize`, which doesn't
  // trigger a React re-render, so the effect never re-fired. Live updates now
  // happen below in `onChunkMeasured`.
  useEffect(() => {
    if (resumableUpload) {
      dispatch(setUploadMetrics({ speedMbs: resumableUpload.speed, chunkSize: resumableUpload.chunkSize }));
    }
  }, [resumableUpload]);

  const uploadSpeed = getCachedUploadSpeed();

  // Placeholder-ETA fallback. Until chunk 1 completes there is no measured
  // throughput, so the meter has no ETA to show — on a slow link that's 20+
  // seconds of blank readout. After 8 s without a completed chunk we publish a
  // conservative estimate (2 Mbps floor) purely so the meter has something to
  // display; `onChunkMeasured` overwrites it with the real value the moment
  // chunk 1 lands.
  //
  // This deliberately does NOT set `isSlowConnection`. It used to, which made
  // it the single biggest source of spurious choice sheets: "one chunk took
  // longer than 8 s" is not a measurement, and on a healthy-but-not-fast link
  // it fired on essentially every upload. The sheet is now driven only by
  // sustained, measured throughput — see `onChunkMeasured` and
  // `slowConnectionPolicy`.
  const ETA_FALLBACK_MS = 8000;
  const etaFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearEtaFallback = () => {
    if (etaFallbackTimerRef.current !== null) {
      clearTimeout(etaFallbackTimerRef.current);
      etaFallbackTimerRef.current = null;
    }
  };
  const armEtaFallback = (totalBytes: number) => {
    clearEtaFallback();
    etaFallbackTimerRef.current = setTimeout(() => {
      dispatch(setEtaSeconds(Math.round((totalBytes * 8) / (2 * 1_000_000))));
    }, ETA_FALLBACK_MS);
  };

  // Slow-connection sheet gating. Two rules, both per-upload:
  //   1. `slowTickCountRef` — the link must read as collapsing for
  //      SLOW_CONSECUTIVE_TICKS chunk measurements in a row. One bad reading is
  //      noise (a tower handoff, a backgrounded tab, one oversized part).
  //   2. `slowSheetFiredRef` — once offered, never re-offered for this upload.
  //      The user has already answered the question; re-asking mid-upload after
  //      they chose Continue (or after the line recovers and dips again) is the
  //      interruption we're trying to remove.
  // Both reset only when a *new* upload begins — not on resume/restart, which
  // is a continuation of the same upload and the same already-answered offer.
  const slowTickCountRef = useRef(0);
  const slowSheetFiredRef = useRef(false);
  const resetSlowSheetState = () => {
    slowTickCountRef.current = 0;
    slowSheetFiredRef.current = false;
    progressSamplesRef.current = [];
    observeStartAtRef.current = Date.now();
    slowSustainedSinceRef.current = null;
  };

  // Per-chunk callback that drives ETA + slow-connection slice fields + the
  // visible Mbps / chunk-size readout in MeterLine. Fires after every chunk
  // completes, with values straight from the Upload instance — bypasses the
  // mutation-doesn't-trigger-re-render trap that the useEffect above used to
  // fall into. Slow thresholds and their calibration history live in
  // `slowConnectionPolicy`.
  const onChunkMeasured = (m: ChunkMeasurement) => {
    // First real chunk measurement has arrived — the placeholder-ETA timer is
    // no longer needed; the dispatches below carry the real signal.
    clearEtaFallback();
    dispatch(setEtaSeconds(m.etaSeconds));
    // Live throughput + chunk size for MeterLine. `chunkBytes` is the size of
    // the chunk that just landed; as `adjustChunkSize` grows it across the
    // upload, this number grows visibly in the UI too.
    lastSpeedMbsRef.current = m.mbpsEwma;
    dispatch(setUploadMetrics({ speedMbs: m.mbpsEwma, chunkSize: m.chunkBytes }));
    // Once bytes are done, the slow-network offer is no longer meaningful —
    // there's nothing left to pause. Reset so the sheet closes immediately
    // rather than sticking around through Finalizing / Questions.
    if (m.uploadedBytes >= m.totalBytes) {
      slowTickCountRef.current = 0;
      dispatch(setIsSlowConnection(false));
      return;
    }

    const collapsingNow = isCollapsingConnection(m.mbpsEwma, m.etaSeconds);
    slowTickCountRef.current = collapsingNow ? slowTickCountRef.current + 1 : 0;

    if (slowSheetFiredRef.current) {
      // Offer already made for this upload — never raise the flag again. We do
      // still let it fall back to false when the link recovers, so the phase
      // copy stays honest and any still-open sheet closes on its own once
      // progress resumes.
      if (!collapsingNow) dispatch(setIsSlowConnection(false));
      return;
    }

    if (slowTickCountRef.current >= SLOW_CONSECUTIVE_TICKS) {
      slowSheetFiredRef.current = true;
      dispatch(setIsSlowConnection(true));
    }
  };

  // ── Stall watchdog ─────────────────────────────────────────────────────────
  // Backstop for an upload whose progress has frozen far longer than any chunk
  // PUT could legitimately take. Chunk PUTs self-abort at `stallTimeoutMs`
  // (Upload.putChunkWithProgress) and exhaust their retry budget — and the
  // resume probe is bounded too — so a mid-chunk stall already clears the
  // in-progress lock and routes into the error→recovery path on its own, well
  // within WATCHDOG_STALL_MS. If we STILL see the lock held with no forward
  // motion past that window, the upload is wedged in a non-chunk wait (URL mint,
  // video metadata read) that nothing else times out — and a held lock blocks
  // every recovery trigger (online / visibility / 5s retry) from restarting.
  // Release the lock and flip to the error state so the existing recovery loop
  // (UploadFlowCarousel's effect on isUploadError) takes it from here.
  const WATCHDOG_STALL_MS = 60000;
  const WATCHDOG_TICK_MS = 5000;
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof window === 'undefined') return;
      if (localStorage.getItem('isUploadInProgress') !== 'true') return;
      // No baseline yet — no upload has started this session.
      if (lastProgressAtRef.current === 0) return;
      const sinceMs = Date.now() - lastProgressAtRef.current;
      if (sinceMs < WATCHDOG_STALL_MS) return;
      console.warn(`[UploadWatchdog] progress frozen ${Math.round(sinceMs / 1000)}s — forcing recovery`);
      trackEventRef.current({
        action: EventNames.upload_watchdog_recovered,
        stalledForMs: sinceMs,
        stalled_for_ms: sinceMs,
        ...getUploadDimensions(),
      });
      // Debounce so we don't re-fire every tick while recovery spins up.
      lastProgressAtRef.current = Date.now();
      // Release the lock that's blocking restartUpload's guard, then arm the
      // existing auto-retry loop. setUploadError(true) is what kicks it off.
      localStorage.setItem('isUploadInProgress', '');
      dispatch(setUploadError(true));
    }, WATCHDOG_TICK_MS);
    return () => clearInterval(id);
  }, [dispatch]);

  const restartUpload = async (propupload?: Upload, propfile?: File, propUrl?: string, shouldPostStory = false) => {
    if (isRestartingRef.current) {
      return;
    }
    isRestartingRef.current = true;
    try {
      const isUploadInProgress = localStorage.getItem('isUploadInProgress');
      if (isUploadInProgress) {
        return;
      }
      localStorage.setItem('isUploadInProgress', 'true');
      // Every restart path (Wi-Fi resume, online recovery, visibility back,
      // 5s error retry) goes through here. Show "Preparing your video for
      // upload" until the first chunk actually moves.
      firstProgressRef.current = true;
      lastProgressAtRef.current = Date.now();
      dispatch(setIsResumingUpload(true));
      const localupload = propupload || resumableUpload;
      const uploadIdToCheck = localupload?.id || activeUploadIdRef.current;
      if (uploadIdToCheck && isUploadCompleted(uploadIdToCheck)) {
        localStorage.setItem('isUploadInProgress', '');
        // File upload is done but story POST may have failed — retry if pending
        if (pendingStoryRetryRef.current) {
          try {
            await postStory(pendingStoryRetryRef.current);
            pendingStoryRetryRef.current = null;
            dispatch(resetUploadingIterations());
            dispatch(setUploadError(false));
            dispatch(setIsUploadInPorgress(false));
          } catch {
            // Still offline — will retry on next restart call
          }
        }
        return;
      }

      const localfile = propfile || resumeFile;
      const localurla = propUrl || resumeUrl;
      if (!localfile) {
        localStorage.setItem('isUploadInProgress', '');
        return;
      }
      let localurl = '';
      if (!localurla) {
        try {
          const res = await createResumableUploadUrl(localfile.type);
          if (!res) {
            localStorage.setItem('isUploadInProgress', '');
            return;
          }
          dispatch(changeStep(UploadProcessStep.VideoUploading));
          localurl = res.resumableUploadUrl;
          setResumeUrl(res.resumableUploadUrl);
          // Refresh the resume record's URL — if a record exists, the upload
          // is starting over from byte 0 against a fresh session.
          patchPendingUpload({
            uploadUrl: res.resumableUploadUrl,
            uploadedBytes: 0,
            status: 'in_progress',
          });
        } catch (err) {
          trackEvent({
            action: EventNames.Creating_resumable_failure,
            accountId: account.id,
            errorMessage: JSON.stringify(err),
            userId: id,
            url: localurl.split('?')[0],
          });
          trackEvent({
            action: EventNames.preflight_failed,
            failed_step: 'create_upload_url',
            error_code: 'mint_error',
            will_retry: true,
          });
          dispatch(setUploadError(true));
          localStorage.setItem('isUploadInProgress', '');
          return;
        }
      }
      localurl = localurl || localurla;
      let upload;
      if (!localupload) {
        upload = new Upload({
          id: crypto.randomUUID(),
          uploadUrl: localurl,
          file: localfile,
          initialChunkSize: SMALL_CHUNK_BYTES,
          // contentType: localfile.type || 'application/octet-stream',
          onProgress: wrappedOnProgress,
          onChunkMeasured,
          trackEvent,
        });
        setResumableUpload(upload);
      }
      dispatch(setUploadError(false));
      const duration = await getVideoDuration(localfile);
      const videoAr = await getVideoAspectRatio(localfile);
      const shouldFill = !isVideoARCloseToTargetAR(videoAr);

      try {
        armEtaFallback(localfile.size);
        try {
          if (localupload) await localupload.start();
          else if (upload) {
            await upload.start();
          }
        } catch (err) {
          // User paused during the restart attempt (e.g., tapped Wi-Fi-later
          // again, or the same pause that triggered this restart hasn't
          // cleared yet). Short-circuit without dispatching completion or
          // posting the story.
          if (err instanceof UploadPausedError) {
            localStorage.setItem('isUploadInProgress', '');
            return;
          }
          // Session URI expired (the common case after a 7-day gap on resume).
          // Mint a fresh URL with the same file and retry once.
          if (err instanceof SessionExpiredError) {
            trackEvent({
              action: EventNames.upload_session_expired,
              accountId: account.id,
              userId: id,
              url: localurl.split('?')[0],
              ...getUploadDimensions(),
            });
            const fresh = await createResumableUploadUrl(localfile.type);
            if (!fresh) {
              dispatch(setUploadError(true));
              localStorage.setItem('isUploadInProgress', '');
              return;
            }
            localurl = fresh.resumableUploadUrl;
            setResumeUrl(localurl);
            const freshUpload = new Upload({
              id: crypto.randomUUID(),
              uploadUrl: localurl,
              file: localfile,
              onProgress: wrappedOnProgress,
              onChunkMeasured,
              trackEvent,
            });
            setResumableUpload(freshUpload);
            activeUploadIdRef.current = freshUpload.id;
            patchPendingUpload({
              id: freshUpload.id,
              uploadUrl: localurl,
              uploadedBytes: 0,
              status: 'in_progress',
            });
            try {
              await freshUpload.start();
            } catch (retryErr) {
              if (retryErr instanceof UploadPausedError) {
                localStorage.setItem('isUploadInProgress', '');
                return;
              }
              trackEvent({
                action: EventNames.Upload_Failed,
                accountId: account.id,
                errorMessage: JSON.stringify(retryErr),
                userId: id,
                url: localurl.split('?')[0],
              });
              // Second attempt with a fresh URL also failed — not recoverable.
              dispatch(setUploadFatal(true));
              localStorage.setItem('isUploadInProgress', '');
              return;
            }
          } else {
            //@ts-ignore
            if (err?.name !== 'UploadAlreadyFinishedError' && err?.name !== 'UploadAlreadyInProgressError') {
              trackEvent({
                action: EventNames.Upload_Failed,
                accountId: account.id,
                errorMessage: JSON.stringify(err),
                userId: id,
                url: localurl.split('?')[0],
              });
              dispatch(setUploadError(true));
              localStorage.setItem('isUploadInProgress', '');
            }
            return;
          }
        }
        dispatch(setIsUploadInPorgress(false));
        localStorage.setItem('isUploadInProgress', '');
        trackEvent({ action: EventNames.Upload_Completed, ...getUploadDimensions() });
        const downloadUrl = localurl.split('?')[0];

        const url = getSyncUpdatedVideoUrl(
          localurl.split('?')[0],
          logoSrc && logoEnabled ? attributesFilterUrl : undefined,
          0,
          trackEvent,
          shouldFill,
        );
        const newThumbnailUrl = getVideoPosterWithCloudinary(url || '', trackEvent) || '';

        const urlWithoutLogo = getSyncUpdatedVideoUrl(downloadUrl, undefined, undefined, trackEvent);
        const altThumbnailUrl = getVideoPosterWithCloudinary(urlWithoutLogo || '', trackEvent);

        const story: CreateStoryRequest = {
          categoryId: categoryId || '',
          originalFileUrl: localurl.split('?')[0],
          url: url,
          thumbnailUrl: newThumbnailUrl,
          altThumbnailUrl,
          isPublic: true,
          duration,
          details: {
            ...DEFAULT_STORY_DETAILS(),
            ctaBtnLinkUrl: returnUrl,
          },
          webappRecorded: false,
          clientRequestId: activeUploadIdRef.current ?? undefined,
        };
        if (!shouldPostStory) return story;

        pendingStoryRetryRef.current = story;
        await postStory(story);
        pendingStoryRetryRef.current = null;
        dispatch(resetUploadingIterations());
        return story;
      } catch (error) {
        trackEvent({
          action: EventNames.Upload_Failed,
          accountId: account.id,
          errorMessage: JSON.stringify(error),
          userId: id,
          url: localurl.split('?')[0],
        });
        // Outer catch — restartUpload's post-bytes path failed. Not recoverable.
        dispatch(setUploadFatal(true));
        return undefined;
      }
    } finally {
      isRestartingRef.current = false;
    }
  };

  const handleUploadedFile = async ({
    file,
    details,
    isPublic,
    videoPos,
    shouldPostStory,
    webappRecorded,
    resumeFrom,
  }: HandleSubmitFileProps): Promise<CreateStoryRequest | undefined> => {
    if (!file) return;

    // Validate that we're uploading to the correct account for this slug
    const currentSlug = router.query.slug as string;
    const isValidUpload = validateSlugAccountWithSubdomain(currentSlug, account.shortcode, account.subdomain);

    if (!isValidUpload) {
      console.log('currentSlug', currentSlug);
      console.log('account.shortcode', account);
      console.error('Upload blocked due to account/slug mismatch');
      // Slug guard rejection — no retry can recover this.
      dispatch(setUploadFatal(true));
      return;
    }

    // Real upload is starting — kill any in-flight background speed-test PUTs
    // so they stop competing for upstream bandwidth.
    abortSpeedTest('upload_started');

    setResumeFile(file);
    storyPostedRef.current = false;
    dispatch(setIsUploadInPorgress(true));
    dispatch(setUploadError(false));
    dispatch(setUploadFatal(false));
    // New upload — the slow-connection offer is available again (it's one-shot
    // per upload, not per session). Clear any sticky flag left by a previous
    // upload that ended while slow.
    resetSlowSheetState();
    dispatch(setIsSlowConnection(false));
    localStorage.setItem('isUploadInProgress', 'true');
    // Fresh upload — zero the displayed progress from the previous one. A
    // stale 100 here both mislabels the analytics pause-tracking below and
    // feeds the percent-inferred completion class of bugs.
    dispatch(setProgress(0));
    // Show the upload meter immediately. Previously the meter only rendered
    // once `currentStep` became `VideoUploading` (dispatched after auth +
    // URL mint), which on slow networks meant 3–10 s of "where's the meter?"
    // confusion behind the opaque IdentityBlock overlay. PrepareVideo also
    // satisfies VideoToolPage's render gate so the UI shows up immediately.
    dispatch(changeStep(UploadProcessStep.PrepareVideo));
    // Arm the first-progress gate. Setting `isResumingUpload` flips the UI
    // into the "Preparing your video for upload" phase regardless of whether
    // this is a fresh upload or a cross-session resume — both flows have a
    // pre-chunk-loop setup window that needs a UI affordance. The flag
    // clears as soon as the first chunk's bytes hit the wire (see
    // `wrappedOnProgress`), at which point copy returns to the normal
    // Uploading state.
    firstProgressRef.current = true;
    lastProgressAtRef.current = Date.now();
    dispatch(setIsResumingUpload(true));
    // Seed the meter's ETA from the cached speed test so there's a number on
    // screen before chunk 1 lands. This no longer opens the choice sheet: the
    // cached result is a single, possibly stale reading (the user may have
    // switched networks since), and acting on it was one of the ways the sheet
    // fired on uploads that were going to finish fine. The sheet now waits for
    // SLOW_CONSECUTIVE_TICKS of real measurements — see `onChunkMeasured`.
    if (uploadSpeed && uploadSpeed.speedMbps > 0) {
      dispatch(setEtaSeconds(Math.round((file.size * 8) / (uploadSpeed.speedMbps * 1_000_000))));
    }
    // Start the URL mint in parallel with the local metadata reads: the mint
    // needs only the MIME type (known now), and serializing it behind
    // duration/aspect-ratio decoding cost 1–3s of dead time before the first
    // byte on every upload — on modest connections, the largest fixed cost we
    // control. The stray .catch() keeps an early rejection from surfacing as
    // an unhandled-rejection warning; the real error still lands at the await
    // inside the try, on the same path it always did.
    const mintStartedAt = performance.now();
    const mintPromise = resumeFrom
      ? Promise.resolve({ resumableUploadUrl: resumeFrom.uploadUrl })
      : createResumableUploadUrl(file.type);
    mintPromise.catch(() => {});
    const metaStartedAt = performance.now();
    const timed = async <T,>(step: string, p: Promise<T>): Promise<T> => {
      try {
        const v = await p;
        trackEvent({
          action: EventNames.preflight_step,
          step,
          outcome: 'ok',
          ms: Math.round(performance.now() - metaStartedAt),
        });
        return v;
      } catch (err) {
        trackEvent({
          action: EventNames.preflight_step,
          step,
          outcome: 'fail',
          ms: Math.round(performance.now() - metaStartedAt),
        });
        trackEvent({
          action: EventNames.preflight_failed,
          failed_step: step,
          error_code: 'metadata_error',
          will_retry: false,
        });
        throw err;
      }
    };
    const [duration, videoAr] = await Promise.all([
      timed('metadata_duration', getVideoDuration(file)),
      timed('metadata_aspect', getVideoAspectRatio(file)),
    ]);
    const shouldFill = !isVideoARCloseToTargetAR(videoAr);
    try {
      // Cross-session resume reuses the persisted URI; fresh starts mint one.
      let res: Awaited<typeof mintPromise>;
      try {
        res = await mintPromise;
        trackEvent({
          action: EventNames.preflight_step,
          step: 'create_upload_url',
          outcome: resumeFrom ? 'skipped' : res ? 'ok' : 'fail',
          ms: Math.round(performance.now() - mintStartedAt),
        });
      } catch (mintErr) {
        trackEvent({
          action: EventNames.preflight_step,
          step: 'create_upload_url',
          outcome: 'fail',
          ms: Math.round(performance.now() - mintStartedAt),
        });
        trackEvent({
          action: EventNames.preflight_failed,
          failed_step: 'create_upload_url',
          error_code: 'mint_error',
          will_retry: true,
        });
        throw mintErr;
      }
      if (!res) {
        trackEvent({
          action: EventNames.preflight_failed,
          failed_step: 'create_upload_url',
          error_code: 'empty_response',
          will_retry: false,
        });
        localStorage.setItem('isUploadInProgress', '');
        return;
      }
      dispatch(changeStep(UploadProcessStep.VideoUploading));

      setResumeUrl(res.resumableUploadUrl);

      // Every upload opens with SMALL_CHUNK_BYTES, landing reading or not, so
      // chunk 1 completes fast and hands the EWMA a real measurement. The
      // landing reading used to size this part (≥5 MiB above 1.5 Mbps), but
      // it is one sample, possibly from another network: 5 MiB is ~10 s at
      // the 4.2 Mbps median with no part-level feedback, and in Aug–Sep 67 of
      // 158 failed attempts sent a first part that never landed. 1 MiB is ~2 s
      // there. Fast lines pay one small part and are back to full size by
      // part 2–3. The reading still seeds the ETA above.
      const chunkSize = SMALL_CHUNK_BYTES;
      setAttemptMeta({ speedSeedMbps: uploadSpeed?.speedMbps ?? null });
      const uploadId = resumeFrom?.id ?? crypto.randomUUID();
      let upload = new Upload({
        id: uploadId,
        uploadUrl: res.resumableUploadUrl,
        file: file,
        initialChunkSize: chunkSize,
        onProgress: wrappedOnProgress,
        onChunkMeasured,
        trackEvent,
      });
      activeUploadIdRef.current = upload.id;
      setResumableUpload(upload);

      // Write or refresh the resume record. For a fresh start this is the
      // first write; for cross-session resume we leave the persisted
      // uploadedBytes alone (server-side 308 probe is the source of truth).
      if (resumeFrom) {
        patchPendingUpload({
          status: 'in_progress',
          updatedAt: Date.now(),
        });
      } else {
        const now = Date.now();
        const initialRecord: PendingUpload = {
          id: upload.id,
          uploadUrl: res.resumableUploadUrl,
          accountId: account.id,
          slug: currentSlug || null,
          file: fingerprintFile(file),
          mimeType: file.type,
          attemptId: getAttempt()?.id ?? null,
          uploadedBytes: 0,
          totalBytes: file.size,
          chunkSize,
          mbpsEwma: null,
          createdAt: now,
          updatedAt: now,
          status: 'in_progress',
        };
        writePendingUpload(initialRecord);
      }
      armEtaFallback(file.size);
      try {
        await upload.start();
      } catch (err) {
        // User halted the upload via Wi-Fi-later. Leave Redux's
        // `isUploadInprogress` flag set so the UI stays in the upload phase
        // (the sticky `isPausedForWifi` selector drives the PausedWifi
        // banner). Clear the localStorage guard so the eventual Resume can
        // re-enter `restartUpload`. Do NOT post the story — the GCS object
        // is unfinalized.
        if (err instanceof UploadPausedError) {
          localStorage.setItem('isUploadInProgress', '');
          return;
        }
        // Session URI expired (7-day GCS TTL). Mint a fresh URL with the same
        // file and try once more before surfacing a fatal error.
        if (err instanceof SessionExpiredError) {
          trackEvent({
            action: EventNames.upload_session_expired,
            accountId: account.id,
            userId: id,
            url: res.resumableUploadUrl.split('?')[0],
            ...getUploadDimensions(),
          });
          const fresh = await createResumableUploadUrl(file.type);
          if (!fresh) {
            dispatch(setUploadError(true));
            localStorage.setItem('isUploadInProgress', '');
            return;
          }
          res = fresh;
          setResumeUrl(res.resumableUploadUrl);
          upload = new Upload({
            id: crypto.randomUUID(),
            uploadUrl: res.resumableUploadUrl,
            file: file,
            initialChunkSize: chunkSize,
            onProgress: wrappedOnProgress,
            onChunkMeasured,
            trackEvent,
          });
          activeUploadIdRef.current = upload.id;
          setResumableUpload(upload);
          // Point the resume record at the fresh URL and reset progress —
          // the new session starts from byte 0.
          patchPendingUpload({
            id: upload.id,
            uploadUrl: res.resumableUploadUrl,
            uploadedBytes: 0,
            status: 'in_progress',
          });
          try {
            await upload.start();
          } catch (retryErr) {
            if (retryErr instanceof UploadPausedError) {
              localStorage.setItem('isUploadInProgress', '');
              return;
            }
            trackEvent({
              action: EventNames.Upload_Failed,
              accountId: account.id,
              errorMessage: JSON.stringify(retryErr),
              userId: id,
              url: res.resumableUploadUrl.split('?')[0],
            });
            // Second attempt with a fresh URL also failed — not recoverable.
            dispatch(setUploadFatal(true));
            localStorage.setItem('isUploadInProgress', '');
            return;
          }
        } else {
          console.log('error', err);
          //@ts-ignore
          if (['FileAlreadyUploadedError', 'UploadAlreadyFinishedError'].includes(err?.name) ? false : true) {
            trackEvent({
              action: EventNames.Upload_Failed,
              accountId: account.id,
              errorMessage: JSON.stringify(err),
              userId: id,
              url: res.resumableUploadUrl.split('?')[0],
            });
            dispatch(setUploadError(true));
            localStorage.setItem('isUploadInProgress', '');
          }
          return;
        }
      }
      dispatch(setIsUploadInPorgress(false));
      localStorage.setItem('isUploadInProgress', '');

      trackEvent({ action: EventNames.Upload_Completed, ...getUploadDimensions() });
      const downloadUrl = res.resumableUploadUrl.split('?')[0];
      const url = getSyncUpdatedVideoUrl(
        res.resumableUploadUrl.split('?')[0],
        logoSrc && logoEnabled ? attributesFilterUrl : undefined,
        videoPos.width,
        trackEvent,
        shouldFill,
      );

      //TODO: Below to worker
      const newThumbnailUrl = getVideoPosterWithCloudinary(url || '', trackEvent) || '';

      const urlWithoutLogo = getSyncUpdatedVideoUrl(downloadUrl, undefined, undefined, trackEvent);
      const altThumbnailUrl = getVideoPosterWithCloudinary(urlWithoutLogo || '', trackEvent);

      const story: CreateStoryRequest = {
        categoryId: categoryId || '',
        url: url,
        originalFileUrl: res.resumableUploadUrl.split('?')[0],
        thumbnailUrl: newThumbnailUrl,
        altThumbnailUrl,
        isPublic: isPublic,
        duration,
        details: {
          ...details,
          ctaBtnLinkUrl: isAthleticSolutionsClient ? returnUrl : details.ctaBtnLinkUrl || null,
        },
        webappRecorded,
        clientRequestId: upload.id,
      };

      if (!shouldPostStory) return story;

      pendingStoryRetryRef.current = story;
      await postStory(story);
      pendingStoryRetryRef.current = null;
      dispatch(resetUploadingIterations());
      return story;
    } catch (error) {
      // console.log('error caught here', error);
      trackEvent({
        action: EventNames.Upload_Failed,
        accountId: account.id,
        errorMessage: JSON.stringify(error),
        userId: id,
      });
      // Post-bytes failure path — retrying the upload won't fix this.
      dispatch(setUploadFatal(true));
      localStorage.setItem('isUploadInProgress', '');
      return undefined;
    }
  };

  const handleSubmitVideo = async (
    blobUrl: string | undefined,
    details: UploadStoryDetails,
    isPublic: boolean,
    supportedMimeType: string,
    smsNotificationRef?: MutableRefObject<boolean | undefined>,
    webappRecorded?: boolean,
  ) => {
    console.log(supportedMimeType);
    if (!blobUrl) return;
    // Fetch the Blob from the Blob URL
    const response = await fetch(blobUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch Blob from URL: ${response.statusText}`);
    }
    const blob = await response.blob();

    // Convert Blob to File
    const file = new File([blob], 'filename', { type: blob.type });

    if (!file) return;

    // Real upload is starting — kill any in-flight background speed-test PUTs
    // so they stop competing for upstream bandwidth.
    abortSpeedTest('upload_started');

    setResumeFile(file);
    storyPostedRef.current = false;
    dispatch(setIsUploadInPorgress(true));
    dispatch(setUploadError(false));
    dispatch(setUploadFatal(false));
    // New upload — see handleUploadedFile. Same one-shot-per-upload reset.
    resetSlowSheetState();
    dispatch(setIsSlowConnection(false));
    localStorage.setItem('isUploadInProgress', 'true');
    dispatch(setProgress(0));
    // Render the upload meter immediately — same rationale as
    // handleUploadedFile. The recorded-video path also has a setup window
    // (blob → File conversion already happened, but URL mint still pending)
    // where the user would otherwise see nothing.
    dispatch(changeStep(UploadProcessStep.PrepareVideo));
    firstProgressRef.current = true;
    dispatch(setIsResumingUpload(true));
    // Same mint/metadata parallelization as the uploaded-file path above.
    // Recorder path: the clip's duration is known from the recorder and the
    // aspect ratio is fixed, so the metadata steps are genuinely skipped.
    trackEvent({ action: EventNames.preflight_step, step: 'metadata_duration', outcome: 'skipped', ms: 0 });
    trackEvent({ action: EventNames.preflight_step, step: 'metadata_aspect', outcome: 'skipped', ms: 0 });
    const recordMintStartedAt = performance.now();
    const mintPromise = createResumableUploadUrl(file.type).then(
      (res) => {
        trackEvent({
          action: EventNames.preflight_step,
          step: 'create_upload_url',
          outcome: res ? 'ok' : 'fail',
          ms: Math.round(performance.now() - recordMintStartedAt),
        });
        return res;
      },
      (err) => {
        trackEvent({
          action: EventNames.preflight_step,
          step: 'create_upload_url',
          outcome: 'fail',
          ms: Math.round(performance.now() - recordMintStartedAt),
        });
        trackEvent({
          action: EventNames.preflight_failed,
          failed_step: 'create_upload_url',
          error_code: 'mint_error',
          will_retry: true,
        });
        throw err;
      },
    );
    mintPromise.catch(() => {});
    const [duration, videoAr] = await Promise.all([getVideoDuration(file), getVideoAspectRatio(file)]);
    const shouldFill = !isVideoARCloseToTargetAR(videoAr);

    try {
      let res = await mintPromise;
      if (!res) {
        localStorage.setItem('isUploadInProgress', '');
        return;
      }
      dispatch(changeStep(UploadProcessStep.VideoUploading));

      setResumeUrl(res.resumableUploadUrl);

      let upload = new Upload({
        id: crypto.randomUUID(),
        uploadUrl: res.resumableUploadUrl,
        file: file,
        initialChunkSize: SMALL_CHUNK_BYTES,
        onProgress: wrappedOnProgress,
        onChunkMeasured,
        trackEvent,
      });
      activeUploadIdRef.current = upload.id;
      setResumableUpload(upload);

      // Initial resume record for the recorded-video path.
      {
        const recSlug = router.query.slug as string | undefined;
        const now = Date.now();
        writePendingUpload({
          id: upload.id,
          uploadUrl: res.resumableUploadUrl,
          accountId: account.id,
          slug: recSlug || null,
          file: fingerprintFile(file),
          mimeType: file.type,
          attemptId: getAttempt()?.id ?? null,
          uploadedBytes: 0,
          totalBytes: file.size,
          chunkSize: SMALL_CHUNK_BYTES, // the opener above; updated as adjustChunkSize runs
          mbpsEwma: null,
          createdAt: now,
          updatedAt: now,
          status: 'in_progress',
        });
      }

      armEtaFallback(file.size);
      try {
        await upload.start();
      } catch (err) {
        // See handleUploadedFile for the rationale — pause is a benign halt,
        // not a failure. Same handling on the recorded-video path.
        if (err instanceof UploadPausedError) {
          localStorage.setItem('isUploadInProgress', '');
          return;
        }
        if (err instanceof SessionExpiredError) {
          trackEvent({
            action: EventNames.upload_session_expired,
            accountId: account.id,
            userId: id,
            url: res.resumableUploadUrl.split('?')[0],
            ...getUploadDimensions(),
          });
          const fresh = await createResumableUploadUrl(file.type);
          if (!fresh) {
            dispatch(setUploadError(true));
            localStorage.setItem('isUploadInProgress', '');
            return;
          }
          res = fresh;
          setResumeUrl(res.resumableUploadUrl);
          upload = new Upload({
            id: crypto.randomUUID(),
            uploadUrl: res.resumableUploadUrl,
            file: file,
            onProgress: wrappedOnProgress,
            onChunkMeasured,
            trackEvent,
          });
          activeUploadIdRef.current = upload.id;
          setResumableUpload(upload);
          patchPendingUpload({
            id: upload.id,
            uploadUrl: res.resumableUploadUrl,
            uploadedBytes: 0,
            status: 'in_progress',
          });
          try {
            await upload.start();
          } catch (retryErr) {
            if (retryErr instanceof UploadPausedError) {
              localStorage.setItem('isUploadInProgress', '');
              return;
            }
            trackEvent({
              action: EventNames.Upload_Failed,
              accountId: account.id,
              errorMessage: JSON.stringify(retryErr),
              userId: id,
              url: res.resumableUploadUrl.split('?')[0],
            });
            // Second attempt with a fresh URL also failed — not recoverable.
            dispatch(setUploadFatal(true));
            localStorage.setItem('isUploadInProgress', '');
            return;
          }
        } else {
          //@ts-ignore
          if (err?.name !== 'UploadAlreadyFinishedError') {
            trackEvent({
              action: EventNames.Upload_Failed,
              accountId: account.id,
              errorMessage: JSON.stringify(err),
              userId: id,
              url: res.resumableUploadUrl.split('?')[0],
            });
            dispatch(setUploadError(true));
            localStorage.setItem('isUploadInProgress', '');
          }
          return;
        }
      }
      dispatch(setIsUploadInPorgress(false));
      localStorage.setItem('isUploadInProgress', '');

      trackEvent({ action: EventNames.Upload_Completed, ...getUploadDimensions() });
      const downloadUrl = res.resumableUploadUrl.split('?')[0];
      const url = getSyncUpdatedVideoUrl(
        res.resumableUploadUrl.split('?')[0],
        logoSrc && logoEnabled ? attributesFilterUrl : undefined,
        undefined,
        trackEvent,
        shouldFill,
      );

      //TODO: Below to worker
      const newThumbnailUrl = getVideoPosterWithCloudinary(url || '', trackEvent) || '';

      const urlWithoutLogo = getSyncUpdatedVideoUrl(downloadUrl, undefined, undefined, trackEvent);
      const altThumbnailUrl = getVideoPosterWithCloudinary(urlWithoutLogo || '', trackEvent);

      const story: CreateStoryRequest = {
        categoryId: categoryId || '',
        url: url,
        originalFileUrl: res.resumableUploadUrl.split('?')[0],
        thumbnailUrl: newThumbnailUrl,
        altThumbnailUrl,
        isPublic: isPublic,
        duration,
        sendSMSNotification: smsNotificationRef?.current || false,
        details: {
          ...details,
          ctaBtnLinkUrl: isAthleticSolutionsClient ? returnUrl : details.ctaBtnLinkUrl || null,
        },
        webappRecorded,
        clientRequestId: upload.id,
      };

      pendingStoryRetryRef.current = story;
      await postStory(story);
      pendingStoryRetryRef.current = null;
      dispatch(resetUploadingIterations());
      return story;
    } catch (error) {
      trackEvent({
        action: EventNames.Upload_Failed,
        accountId: account.id,
        errorMessage: JSON.stringify(error),
        userId: id,
      });
      // Post-bytes failure path — retrying the upload won't fix this.
      dispatch(setUploadFatal(true));
      localStorage.setItem('isUploadInProgress', '');
      return undefined;
    }
  };

  // Nudge the running upload instead of restarting it: see Upload.wake.
  const wakeUpload = (reason: WakeReason): Promise<WakeOutcome> =>
    resumableUpload ? resumableUpload.wake(reason) : Promise.resolve('noop_idle');

  return { handleUploadedFile, handleSubmitVideo, postStory, restartUpload, wakeUpload, resumableUpload, storyPostedRef };
};
